import { Calendar, ChevronRight, Heart, MapPin, Search, Zap } from 'lucide-react';

export default function ViewDecouvrir() {
  return (
    <div className='grid md:grid-cols-2 gap-10 items-start demo-view-enter'>
      {/* Left */}
      <div className='space-y-6'>
        <div className='inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-full'>
          <Zap className='w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400' />
          <span className='text-xs font-bold text-emerald-600 dark:text-emerald-400'>
            Fini les recherches sur Facebook
          </span>
        </div>

        <div>
          <h3 className='text-2xl font-black text-slate-900 dark:text-white mb-2'>Trouvez votre prochain concours</h3>
          <p className='text-slate-500 dark:text-slate-400 text-sm leading-relaxed'>
            Filtrez par ville, format, niveau et dotation. Ajustez le rayon de recherche et visualisez sur la carte
            interactive.
          </p>
        </div>

        {/* Search bar */}
        <div className='bg-slate-100 border border-slate-200 dark:bg-slate-800/80 dark:border-slate-700 rounded-2xl p-3 flex items-center gap-3'>
          <Search className='w-4 h-4 text-slate-400 shrink-0' />
          <span className='text-slate-500 dark:text-slate-300 text-sm flex-1'>Concours à Lyon et alentours…</span>
          <span className='bg-blue-600 text-white text-xs px-3 py-1.5 rounded-xl font-bold'>Rechercher</span>
        </div>

        {/* Active filters */}
        <div className='flex flex-wrap gap-2 items-center'>
          {['Lyon + 30 km', 'Doublette', 'Avec dotation'].map(f => (
            <span
              key={f}
              className='bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-300 text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1.5'
            >
              {f} <span className='text-blue-500 cursor-pointer'>×</span>
            </span>
          ))}
          <span className='text-slate-500 text-xs'>28 concours trouvés</span>
        </div>

        {/* Platform stats */}
        <div className='grid grid-cols-3 gap-3 pt-2'>
          {[
            { v: '1 200+', l: 'concours / an', c: 'text-blue-600 dark:text-blue-400' },
            { v: '47', l: 'départements', c: 'text-emerald-600 dark:text-emerald-400' },
            { v: '0 €', l: 'pour consulter', c: 'text-amber-600 dark:text-amber-400' },
          ].map(({ v, l, c }) => (
            <div
              key={l}
              className='bg-slate-50 border border-slate-200 dark:bg-slate-900/60 dark:border-slate-800 rounded-2xl p-4 text-center'
            >
              <p className={`text-xl font-black ${c}`}>{v}</p>
              <p className='text-[10px] text-slate-500 mt-0.5'>{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right — competition cards */}
      <div className='space-y-3'>
        {/* Card 1: featured */}
        <div className='bg-blue-50/60 border border-blue-200/60 dark:bg-slate-800/60 dark:border-blue-500/30 rounded-2xl p-5 hover:border-blue-400 dark:hover:border-blue-500/60 transition cursor-pointer'>
          <div className='flex items-start gap-3 mb-4'>
            <div className='flex-1'>
              <div className='flex gap-1.5 mb-1.5'>
                <span className='bg-amber-400/10 border border-amber-400/20 text-amber-600 dark:text-amber-400 text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider'>
                  Dotation 3 000 €
                </span>
                <span className='bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-[9px] font-bold px-2 py-0.5 rounded-md uppercase'>
                  Régional
                </span>
              </div>
              <p className='font-bold text-slate-900 dark:text-white text-sm'>Grand Prix Pétanque d'Été — Gerland</p>
              <div className='flex items-center gap-4 mt-1.5 text-slate-500 dark:text-slate-400 text-xs'>
                <span className='flex items-center gap-1'>
                  <MapPin className='w-3 h-3' />
                  Lyon, 69
                </span>
                <span className='flex items-center gap-1'>
                  <Calendar className='w-3 h-3' />
                  13 Juin 2026
                </span>
              </div>
            </div>
            <button className='p-1.5 text-slate-400 dark:text-slate-600 hover:text-red-400 transition'>
              <Heart className='w-4 h-4' />
            </button>
          </div>
          <div className='flex items-center gap-4'>
            <div className='flex-1'>
              <div className='flex justify-between text-xs mb-1'>
                <span className='text-slate-500 dark:text-slate-400'>Places restantes</span>
                <span className='font-bold text-orange-500 dark:text-orange-400'>14 / 128</span>
              </div>
              <div className='h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden'>
                <div className='h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full' />
              </div>
            </div>
            <span className='bg-blue-100 border border-blue-200 text-blue-600 dark:bg-blue-600/20 dark:border-blue-500/30 dark:text-blue-300 text-[10px] px-2.5 py-1 rounded-lg font-bold shrink-0'>
              Doublette
            </span>
          </div>
        </div>

        {/* Card 2: complet */}
        <div className='bg-slate-50 border border-slate-200 dark:bg-slate-800/20 dark:border-slate-800 rounded-2xl p-5 opacity-55'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='font-bold text-slate-900 dark:text-white text-sm'>National de Pétanque — Marseille</p>
              <div className='flex gap-4 mt-1 text-slate-500 text-xs'>
                <span className='flex items-center gap-1'>
                  <MapPin className='w-3 h-3' />
                  Marseille, 13
                </span>
                <span className='flex items-center gap-1'>
                  <Calendar className='w-3 h-3' />
                  21 Juin 2026
                </span>
              </div>
            </div>
            <div className='text-right shrink-0'>
              <p className='text-red-500 dark:text-red-400 text-xs font-black'>COMPLET</p>
              <p className='text-slate-500 dark:text-slate-600 text-[10px] mt-0.5'>Liste d'attente</p>
            </div>
          </div>
        </div>

        {/* Card 3: open gratuit */}
        <div className='bg-white border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700 rounded-2xl p-5 hover:border-slate-300 dark:hover:border-slate-600 transition cursor-pointer'>
          <div className='flex items-center justify-between'>
            <div>
              <div className='mb-1.5'>
                <span className='bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[9px] font-bold px-2 py-0.5 rounded-md uppercase'>
                  Gratuit · Open
                </span>
              </div>
              <p className='font-bold text-slate-900 dark:text-white text-sm'>Open Été — Boulodrome de Bordeaux</p>
              <div className='flex gap-4 mt-1 text-slate-500 dark:text-slate-400 text-xs'>
                <span className='flex items-center gap-1'>
                  <MapPin className='w-3 h-3' />
                  Bordeaux, 33
                </span>
                <span className='flex items-center gap-1'>
                  <Calendar className='w-3 h-3' />5 Juil 2026
                </span>
              </div>
            </div>
            <ChevronRight className='w-5 h-5 text-slate-400 dark:text-slate-600' />
          </div>
        </div>

        <p className='text-center text-slate-500 dark:text-slate-600 text-xs pt-1'>
          + 25 autres concours dans votre zone
        </p>
      </div>
    </div>
  );
}
